import { lazy } from 'react';

import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-components/loadhandlers/Loadable';
import { pointsScannerEnabled } from 'utils/constants';

const SuperAdminPanel = Loadable(lazy(() => import('views/admin/admin-dashboard/SuperAdminPanel')));
const UserAnalytics = Loadable(lazy(() => import('views/admin/user-analytics/UserAnalytics')));
const InfluencerAnalytics = Loadable(lazy(() => import('views/admin/influencer-analytics/InfluencerAnalytics')));
const CreatorAnalytics = Loadable(lazy(() => import('views/admin/creator-analytics/CreatorAnalytics')));
const HostApplications = Loadable(lazy(() => import('views/admin/host-applications')));
const HostPerformance = Loadable(lazy(() => import('views/admin/host-performance')));
const HostPerformanceHost = Loadable(lazy(() => import('views/admin/host-performance/host')));

/*
 * The signed-in travel host area. Here rather than in HostRoutes so it inherits
 * MainLayout — a host keeps the header, the sidebar and the account menu on
 * every page of their own product, the same as every other user type. Only the
 * anonymous screens (signup, login) and the one-time approval landing stay on
 * MinimalLayout; see the note in HostRoutes.jsx.
 */
const HostOnboarding = Loadable(lazy(() => import('views/host-onboarding')));
const PhoneVerificationStep = Loadable(lazy(() => import('views/host-onboarding/steps/PhoneVerificationStep')));
const ProfileStep = Loadable(lazy(() => import('views/host-onboarding/steps/ProfileStep')));
const CredentialsStep = Loadable(lazy(() => import('views/host-onboarding/steps/CredentialsStep')));
const ReviewStep = Loadable(lazy(() => import('views/host-onboarding/steps/ReviewStep')));
const SubmittedState = Loadable(lazy(() => import('views/host-onboarding/SubmittedState')));
const HostServices = Loadable(lazy(() => import('views/host-services')));
const HostPackages = Loadable(lazy(() => import('views/host-packages')));
const HostPackageBuilder = Loadable(lazy(() => import('views/host-packages/builder')));
const HostProfile = Loadable(lazy(() => import('views/host-profile')));
const HostInbox = Loadable(lazy(() => import('views/host-inbox')));
const HostCalendar = Loadable(lazy(() => import('views/host-calendar')));
const HostBookings = Loadable(lazy(() => import('views/host-bookings')));
const Messages = Loadable(lazy(() => import('views/messages')));

const MyApps = Loadable(lazy(() => import('views/my-apps/index')));
const Subscription = Loadable(lazy(() => import('views/subscription/SubscriptionPage')));

const Deals = Loadable(lazy(() => import('views/Deals')));
const Profile = Loadable(lazy(() => import('views/profile/Profile')));

const NotificationSettings = Loadable(lazy(() => import('views/notification-settings/NotificationSettings')));
const Referral = Loadable(lazy(() => import('views/referral/Referral')));
const PointsPortal = Loadable(lazy(() => import('views/points/PointsPortal')));
const PointsScannerUnavailable = Loadable(lazy(() => import('views/points/PointsScannerUnavailable')));

const ItineraryAIForm = Loadable(lazy(() => import('views/ItineraryAIForm')));
const ItineraryAIFinalPlan = Loadable(lazy(() => import('views/ItineraryAIFinalPlan')));
const ItineraryAIFinalPlanEditor = Loadable(lazy(() => import('views/ai-trip-plan-editor/AITripPlanEditor')));

const AiTripPlans = Loadable(lazy(() => import('views/ai-trip-plans/index')));

const InfluencerDashboard = Loadable(lazy(() => import('views/influencer/Influencer')));
const StripeConnectOnboarding = Loadable(lazy(() => import('views/influencer/stripe-connect/StripeConnectOnboarding')));
const StripeConnectBalances = Loadable(lazy(() => import('views/influencer/stripe-connect/StripeConnectBalances')));
const StreamView = Loadable(lazy(() => import('views/stream/StreamView')));
const MyCollection = Loadable(lazy(() => import('views/collection/index')));
const SalesDashboard = Loadable(lazy(() => import('views/sales-dashboard/index')));

const roleRoutes = (routes) =>
  ['influencer', 'creator'].flatMap((role) => routes.map((route) => ({ ...route, path: `${role}/${route.path}` })));

const UserRoutes = {
  path: '/',
  element: <MainLayout />,
  children: [
    {
      path: 'my-apps',
      element: <MyApps />
    },
    {
      path: 'collection',
      element: <MyCollection />
    },
    {
      path: 'subscription',
      element: <Subscription />
    },
    {
      path: 'flight-deal-scanner',
      element: <Deals />
    },
    {
      path: 'profile',
      element: <Profile />
    },
    {
      path: 'notification-settings',
      element: <NotificationSettings />
    },
    {
      path: 'ai-trip-plans',
      element: <AiTripPlans />
    },
    {
      path: 'itinerary-ai',
      element: <ItineraryAIForm />
    },
    {
      path: 'itinerary-ai-plan',
      element: <ItineraryAIFinalPlan />
    },
    {
      path: 'itinerary-ai-plan-editor',
      element: <ItineraryAIFinalPlanEditor />
    },
    {
      path: 'referral',
      element: <Referral />
    },
    {
      path: 'points',
      element: pointsScannerEnabled ? <PointsPortal /> : <PointsScannerUnavailable />
    },
    ...roleRoutes([
      { path: ':childpath', element: <InfluencerDashboard /> },
      { path: 'invite-a-user', element: <Referral /> },
      { path: 'stripe-connect-onboarding', element: <StripeConnectOnboarding /> },
      { path: 'stripe-connect/balances', element: <StripeConnectBalances /> },
      { path: 'spotlight', element: <StreamView /> },
      { path: 'sales-dashboard', element: <SalesDashboard /> }
    ]),
    {
      path: 'admin/super-admin',
      element: <SuperAdminPanel />
    },
    {
      path: 'admin/user-analytics',
      element: <UserAnalytics />
    },
    {
      path: 'admin/influencer-analytics',
      element: <InfluencerAnalytics />
    },
    {
      path: 'admin/creator-analytics',
      element: <CreatorAnalytics />
    },
    {
      // The travel host review queue. Here rather than in HostRoutes so it
      // inherits MainLayout; the page checks `userType` itself (plan §2.2).
      path: 'admin/host-applications',
      element: <HostApplications />
    },
    {
      // Every approved host ranked, and one host's page (admin-host-performance
      // tech design §5). Each page checks `userType` itself, as above.
      path: 'admin/host-performance',
      element: <HostPerformance />
    },
    {
      path: 'admin/host-performance/:hostId',
      element: <HostPerformanceHost />
    },
    {
      path: 'stream',
      element: <StreamView />
    },
    {
      /*
       * The application wizard. One route element with nested step children,
       * not six siblings: the parent mounts HostOnboardingProvider once, so
       * there is one status fetch and no remount flicker between steps.
       */
      path: 'host/onboarding',
      element: <HostOnboarding />,
      children: [
        {
          path: 'phone',
          element: <PhoneVerificationStep />
        },
        {
          // The hub deep-links into this one by hash: #identity, #about,
          // #location, #practice, #experience.
          path: 'profile',
          element: <ProfileStep />
        },
        {
          path: 'credentials',
          element: <CredentialsStep />
        },
        {
          path: 'review',
          element: <ReviewStep />
        },
        {
          // Frozen receipt. The resume rule routes here whenever the
          // application is `submitted`, from any host URL.
          path: 'submitted',
          element: <SubmittedState />
        }
      ]
    },
    {
      // Services nomination. `?type=medical_provider` is a recruiting deep link
      // handled inside the view, not a route.
      path: 'host',
      element: <HostServices />
    },
    {
      path: 'host/packages',
      element: <HostPackages />
    },
    {
      /*
       * Create and edit resolve to the same builder — the only difference is
       * whether `useParams` finds an id. React Router ranks the static segment
       * above the dynamic one, so `/host/packages/new` can never read as a
       * package called "new".
       */
      path: 'host/packages/new',
      element: <HostPackageBuilder />
    },
    {
      path: 'host/packages/:packageId',
      element: <HostPackageBuilder />
    },
    {
      /*
       * The approved host's profile editor (host-profile-tech-design §9.1). A
       * sibling of `host/onboarding`, never a child: the wizard's resume rule
       * sends approved hosts here, so nesting it would bounce it straight back.
       */
      path: 'host/profile',
      element: <HostProfile />
    },
    {
      /*
       * The host inbox (host-inbox-tech-design §9.1): the list, and one
       * enquiry beside it. Both mount the same element, so opening an enquiry
       * keeps the list and its loaded pages. A booking request lives in its
       * thread, so it has no route of its own (host-availability T1).
       */
      path: 'host/inbox',
      element: <HostInbox />
    },
    {
      path: 'host/inbox/:enquiryId',
      element: <HostInbox />
    },
    {
      /*
       * The host's calendar (host-availability-ui-design §3.10): approved
       * hosts only, as the inbox. The month is a query (`?month=2026-12`),
       * never a path segment, so this one route is all there is.
       */
      path: 'host/calendar',
      element: <HostCalendar />
    },
    {
      /*
       * The host's booking ledger (host-bookings-tech-design D1): approved
       * hosts only, as the calendar. The tab, search, filters, sort and page
       * are a query, never path segments, so this one route is all there is.
       */
      path: 'host/bookings',
      element: <HostBookings />
    },
    {
      /*
       * A member's conversations as a guest (host-messaging-tech-design §10.1):
       * the list, and one conversation beside it, on one element so the list
       * keeps its pages. Any signed-in account, a travel host included (R6).
       */
      path: 'messages',
      element: <Messages />
    },
    {
      path: 'messages/:conversationId',
      element: <Messages />
    }
  ]
};

export default UserRoutes;
